import { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PermitApplicationStep, usePermitApplicationContext } from "../PermitApplicationContext";
import { getStepFromPath, getFullPathFromStep, getNextStep, getPreviousStep } from "../constants";

export interface UsePermitApplicationNavigationResult {
  /** Current step derived from URL path */
  currentStep: PermitApplicationStep | null;
  /** Navigate to a specific step if allowed */
  goToStep: (step: PermitApplicationStep) => void;
  /** Navigate to the next step if current step is complete */
  goNext: () => void;
  /** Navigate to the previous step */
  goBack: () => void;
  /** Whether the user can proceed to the next step */
  canGoNext: boolean;
  /** Whether there is a previous step to go back to */
  canGoBack: boolean;
  /** Whether we're on the last step */
  isLastStep: boolean;
  /** Whether we're on the first step */
  isFirstStep: boolean;
}

/**
 * Hook for managing navigation between permit application steps.
 * Reads current step from URL and provides navigation functions.
 */
export function usePermitApplicationNavigation(): UsePermitApplicationNavigationResult {
  const navigate = useNavigate();
  const location = useLocation();
  const { isStepComplete, canProceedToStep } = usePermitApplicationContext();

  const currentStep = useMemo(() => getStepFromPath(location.pathname), [location.pathname]);

  const isFirstStep = currentStep === PermitApplicationStep.SelectAuthority;
  const isLastStep = currentStep === PermitApplicationStep.ReviewConfirm;

  const canGoNext = useMemo(() => {
    if (!currentStep) {
      return false;
    }
    const nextStep = getNextStep(currentStep);
    return nextStep !== null && isStepComplete(currentStep);
  }, [currentStep, isStepComplete]);

  const canGoBack = useMemo(() => {
    if (!currentStep) {
      return false;
    }
    return getPreviousStep(currentStep) !== null;
  }, [currentStep]);

  const goToStep = useCallback(
    (step: PermitApplicationStep) => {
      if (canProceedToStep(step)) {
        navigate(getFullPathFromStep(step));
      }
    },
    [navigate, canProceedToStep],
  );

  const goNext = useCallback(() => {
    if (!currentStep) {
      return;
    }
    const nextStep = getNextStep(currentStep);
    if (nextStep && isStepComplete(currentStep) && canProceedToStep(nextStep)) {
      navigate(getFullPathFromStep(nextStep));
    }
  }, [currentStep, navigate, isStepComplete, canProceedToStep]);

  const goBack = useCallback(() => {
    if (!currentStep) {
      return;
    }
    const previousStep = getPreviousStep(currentStep);
    if (previousStep) {
      navigate(getFullPathFromStep(previousStep));
    }
  }, [currentStep, navigate]);

  return {
    currentStep,
    goToStep,
    goNext,
    goBack,
    canGoNext,
    canGoBack,
    isLastStep,
    isFirstStep,
  };
}

export default usePermitApplicationNavigation;
